import {
  Component,
  DestroyRef,
  effect,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import {
  BehaviorSubject,
  EMPTY,
  Subject,
  catchError,
  firstValueFrom,
  from,
  switchMap,
  takeWhile,
  timer,
} from 'rxjs';
import { MessageService } from '../../../../../../../services/message.service';
import { BatchService } from '../../../../../../../services/batch.service';
import { DocumentService } from '../../../../../../../services/document.service';
import {
  ACTIVE_BATCH_STATUSES,
  Batch,
  BatchDocumentStatus,
  BatchPopulatedDocument,
} from '../../../../../../../models/batch';
import { AnalysisMode } from '../../../../../../../models/uploadFile';
import { Option } from '../../../../../../../components/select/select.component';
import {
  ReanalyzeConfirmationDialogComponent,
  ReanalyzeConfirmationDialogResult,
} from '../../../../../../../dialogs/reanalyze-confirmation-dialog/reanalyze-confirmation-dialog.component';
import { PaginatedListManager } from '../../../../../../../shared/single-crawler/paginated-list.manager';
import { getDocumentStatus } from '../../../../../../../helpers/document-status.helper';
import {
  BatchScore,
  EMPTY_BATCH_SCORE,
  computeBatchScore,
} from '../../../../../../../helpers/batch-score.helper';
import {
  POLL_INTERVAL_MS
} from '../../../../../../../shared/single-crawler/launch-eligibility';
import { UtilsService } from '../../../../../../../services/utils.service';

export interface AnalyzeStepFilters {
  status: BatchDocumentStatus[];
  [key: string]: unknown;
}

interface BatchProgressState {
  batch: Batch;
  progress: number;
}

/**
 * The "Analyze" step. Lists the documents produced by the integration's
 * batches */
@Component({
  selector: 'app-single-crawler-analyze-step',
  templateUrl: './single-crawler-analyze-step.component.html',
  styleUrls: ['./single-crawler-analyze-step.component.scss'],
})
export class SingleCrawlerAnalyzeStepComponent {
  readonly integrationId = input.required<string>();

  selectedBatchId?: string;
  selectedBatch?: Batch;
  selectedStatus?: string | string[];
  progress = 0;
  score: BatchScore = EMPTY_BATCH_SCORE;
  analyzing = false;

  // Mirrors the batch poll's activity (ACTIVE_BATCH_STATUSES + progress < 100)
  // so the docs table auto-refreshes in sync with it. Launch eligibility
  // cannot drive the refresh here: analysis never changes the crawler status.
  private readonly batchActive$ = new BehaviorSubject<boolean>(false);

  readonly statusSelectOptions: Option[] = ([
    BatchDocumentStatus.OK,
    BatchDocumentStatus.ERROR,
    BatchDocumentStatus.NO_TRADEMARK,
    BatchDocumentStatus.NO_NICE_CLASSIFICATION,
    BatchDocumentStatus.NO_TERRITORY,
    BatchDocumentStatus.NO_DATE,
  ] as BatchDocumentStatus[])
    .map((status) => ({
      label: `BATCH.DOCUMENT_${status}`,
      value: status,
      selected: false,
      disabled: false,
    }));

  private readonly columns: string[] = [
    'select',
    'name',
    'class',
    'status',
    'actions',
  ];

  // Documents not analysed yet can be left out of the analysis, or put back.
  readonly selection = new SelectionModel<string>(true, []);

  get displayedColumns(): string[] {
    return this.list.paginator.docs.some((document) => this.isSelectable(document))
      ? this.columns
      : this.columns.filter((column) => column !== 'select');
  }

  readonly list: PaginatedListManager<BatchPopulatedDocument, AnalyzeStepFilters>;

  private readonly batchSelectionSubject = new Subject<string | undefined>();

  constructor(
    private readonly _message: MessageService,
    private readonly batchService: BatchService,
    private readonly documentService: DocumentService,
    private readonly dialog: MatDialog,
    private readonly router: Router,
    private readonly toastr: ToastrService,
    private readonly translate: TranslateService,
    private readonly utils: UtilsService,
    private readonly destroyRef: DestroyRef,
  ) {
    this.list = new PaginatedListManager<BatchPopulatedDocument, AnalyzeStepFilters>({
      destroyRef,
      initialFilters: {
        status: [],
      },
      retrieveFn: async (page, limit, offset, sort, filters) => {
        // No batch selected yet: return an empty page instead of querying with an
        // empty id.
        if (!this.selectedBatchId) {
          return {
            docs: [],
            page,
            offset,
            limit,
            totalDocs: 0,
            sort,
          };
        }
        return this.batchService.retrieveDocuments(
          this.selectedBatchId,
          page,
          limit,
          offset,
          sort,
          filters.status,
        );
      },
      onError: (err) => this.utils.toastError(err),
      onLoadingStart: () => this._message.emitChange('LOADING', 'START'),
      onLoadingEnd: () => this._message.emitChange('LOADING', 'END'),
    });

    // Poll the selected batch's state: fetch immediately, then every
    // POLL_INTERVAL_MS while the batch is still being analyzed. switchMap
    // cancels any in-flight fetch when the selection or a tick fires again.
    this.batchSelectionSubject.pipe(
      switchMap((id) => {
        if (!id) {
          this.selectedBatch = undefined;
          this.progress = 0;
          this.score = EMPTY_BATCH_SCORE;
          this.batchActive$.next(false);
          return EMPTY;
        }
        this.progress = 0;
        return timer(0, POLL_INTERVAL_MS).pipe(
          switchMap(() => from(this.fetchBatchState(id)).pipe(
            // Transient failure: skip the tick, the next one retries.
            catchError(() => EMPTY),
          )),
          // Inclusive takeWhile keeps the final state before polling stops.
          takeWhile((state) => this.isBatchActive(state), true),
        );
      }),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((state) => {
      this.selectedBatch = state.batch;
      this.progress = state.progress;
      this.score = computeBatchScore(state.batch);
      this.batchActive$.next(this.isBatchActive(state));
    });

    effect(() => {
      const integrationId = this.integrationId();
      if (integrationId) {
        void this.list.init();
        // Auto-refresh the docs table in sync with the batch poll so the
        // per-document status chips update while an analysis is running.
        this.list.startAutoRefresh(this.batchActive$);
      }
    });
  }

  private isBatchActive(state: BatchProgressState): boolean {
    return ACTIVE_BATCH_STATUSES.some((s) => s === state.batch.status) &&
      state.progress < 100;
  }

  // Selects a batch for the docs table and the progress stream, clearing the
  // status filter + its select synchronously.
  private selectBatch(id: string | undefined): void {
    this.selectedBatchId = id;
    this.selectedStatus = undefined;
    this.list.filters.status = [];
    this.batchSelectionSubject.next(id);
  }

  private async fetchBatchState(id: string): Promise<BatchProgressState> {
    const [batch, serverProgress] = await Promise.all([
      this.batchService.retrieve(id),
      this.batchService.retrieveProgress(id)
        .then((res) => res.progress)
        .catch(() => undefined),
    ]);
    const progress = serverProgress === undefined
      ? (batch.documents === 0 ? 100 : 0)
      : Math.round(100 * serverProgress);
    return { batch, progress };
  }

  onBatchChange(event?: { value?: Batch }): void {
    const nextId = event?.value?._id;
    if (!nextId || nextId === this.selectedBatchId) return;
    this.selectBatch(nextId);
    this.list.resetPage();
    void this.list.init();
  }

  onStatusChange(event?: { value: Option }): void {
    if (!this.selectedBatchId) return;
    const value = event?.value?.value;
    const statuses = Array.isArray(value)
      ? value
      : value ? [value as BatchDocumentStatus] : [];
    this.selectedStatus = value;
    void this.list.updateFiltersNow({ status: statuses });
  }

  async handlePage(event: PageEvent): Promise<void> {
    await this.list.handlePage(event);
  }

  async onSortChange(event: Sort): Promise<void> {
    await this.list.onSortChange(event);
  }

  documentStatus(document: BatchPopulatedDocument) {
    return getDocumentStatus(document, this.translate);
  }

  documentIcon(document: BatchPopulatedDocument): string {
    return this.documentService.icon(document);
  }

  toDocument(document: BatchPopulatedDocument): void {
    void this.router.navigate(['documents', document._id], {
      state: { from: 'INTEGRATION' },
    });
  }

  // Re-launching analysis on an already-analyzed batch is a re-analysis.
  analyzeLabel(): string {
    return this.selectedBatch?.lastAnalyzedAt
      ? 'ACTIONS.REANALYZE'
      : 'INTEGRATIONS.ANALYZE.LAUNCH_ANALYSIS';
  }

  isSelectable(document: BatchPopulatedDocument): boolean {
    return document.analysis === 'NOT_ANALYZED' || document.analysis === 'EXCLUDED';
  }

  toggleSelection(document: BatchPopulatedDocument): void {
    this.selection.toggle(document._id);
  }

  async setSelectionExcluded(excluded: boolean): Promise<void> {
    const batch = this.selectedBatch;
    if (!batch?._id || this.selection.isEmpty()) return;
    try {
      this._message.emitChange('LOADING', 'START');
      await this.batchService.setAnalysisExcluded(batch._id, this.selection.selected, excluded);
      this.toastr.success(this.translate.instant('INTEGRATIONS.BACKLINK_CRAWLER.SELECTION_UPDATED'));
      this.selection.clear();
      await this.list.retrieve();
    } catch (err) {
      this.utils.toastError(err);
    } finally {
      this._message.emitChange('LOADING', 'END');
    }
  }

  async analyze(): Promise<void> {
    const batch = this.selectedBatch;
    if (!batch?._id) return;
    const dialogRef = this.dialog.open(ReanalyzeConfirmationDialogComponent, {
      panelClass: 'dialog-container',
      width: '480px',
      data: {
        titleKey: 'INTEGRATIONS.ANALYZE.ANALYZE_TITLE',
        text: this.translate.instant('INTEGRATIONS.ANALYZE.ANALYZE_TEXT', { number: batch.number }),
      },
    });
    const result = await firstValueFrom<ReanalyzeConfirmationDialogResult | undefined>(
      dialogRef.afterClosed(),
    );
    if (result?.confirmed) {
      try {
        this.analyzing = true;
        this._message.emitChange('LOADING', 'START');
        await this.batchService.analyze(batch._id, result.analysis ?? AnalysisMode.LIGHT);
        this._message.emitChange('LOADING', 'END');
        this.toastr.success(this.translate.instant('INTEGRATIONS.ANALYZE.ANALYSIS_LAUNCHED'));
        // Restart the polling stream for the now-active batch (same id) and
        // refresh the docs table so re-queued documents show their new status.
        this.selection.clear();
        this.selectBatch(batch._id);
        await this.list.init();
      } catch (err) {
        this._message.emitChange('LOADING', 'END');
        this.utils.toastError(err);
      } finally {
        this.analyzing = false;
      }
    }
  }
}
